import { type JSX, useState } from 'react';
import { Link } from 'react-router-dom';

import { Icon } from '@/components/icons';
import { cn } from '@/lib/utils';
import type { FAQ } from '@/content/help/types';

interface FAQItemProps {
  faq: FAQ;
  /** Open by default — used when search has 1 hit, auto-expand it. */
  defaultOpen?: boolean;
  /** Highlight matching substring inside the question. Optional. */
  highlight?: string;
}

/**
 * Single FAQ row — accordion-style. Click question to expand / collapse.
 * The id is used as an anchor so /help#login deep-links to it.
 */
export function FAQItem({ faq, defaultOpen, highlight }: FAQItemProps): JSX.Element {
  const [open, setOpen] = useState(defaultOpen ?? false);

  return (
    <div id={faq.id} className="border-b border-line last:border-b-0 scroll-mt-20">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 py-3.5 px-1 text-left hover:text-volt-600 transition-colors"
        aria-expanded={open}
      >
        <span className="text-[14px] font-medium text-text">
          {highlight ? renderHighlighted(faq.question, highlight) : faq.question}
        </span>
        <Icon.chevronDown
          width="16"
          height="16"
          className={cn('flex-shrink-0 text-text-3 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <div className="pb-4 pl-1 pr-4 text-[13px] text-text-2 leading-relaxed space-y-2">
          {faq.answer}
          {faq.cta && (
            <div className="pt-1">
              <Link
                to={faq.cta.to}
                className="inline-flex items-center gap-1 text-[12px] font-medium text-volt-600 hover:underline"
              >
                {faq.cta.label}
                <Icon.arrowRight width="12" height="12" />
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Wraps every case-insensitive occurrence of `needle` inside `text` in a
 * <mark> for visual hint. Falls back to plain text when there's no match.
 */
function renderHighlighted(text: string, needle: string): JSX.Element {
  const lower = text.toLowerCase();
  const target = needle.toLowerCase();
  if (!target || !lower.includes(target)) return <>{text}</>;
  const parts: (string | JSX.Element)[] = [];
  let i = 0;
  while (i < text.length) {
    const idx = lower.indexOf(target, i);
    if (idx === -1) {
      parts.push(text.slice(i));
      break;
    }
    if (idx > i) parts.push(text.slice(i, idx));
    parts.push(
      <mark key={`${idx}-${parts.length}`} className="bg-volt/20 text-text rounded-xs px-0.5">
        {text.slice(idx, idx + target.length)}
      </mark>,
    );
    i = idx + target.length;
  }
  return <>{parts}</>;
}
